import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiErrorDetail, setSessionExpiredHandler } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [logoutReason, setLogoutReason] = useState("");
  const userRef = useRef(null);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(async () => {
        try {
          await api.post("/auth/refresh");
          const res = await api.get("/auth/me");
          setUser(res.data);
        } catch {
          setUser(false);
        }
      });
  }, []);

  // Forced logout (e.g. inactivity) -> drop session with an explanatory message.
  // Only applies when a session actually existed; a fresh anonymous visit stays silent.
  useEffect(() => {
    setSessionExpiredHandler((reason) => {
      if (!userRef.current) return;
      const friendly = !reason || reason === "No refresh token" ? "Your session has expired. Please sign in again." : reason;
      setLogoutReason(friendly);
      setUser(false);
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  // Activity ping: any interaction keeps the server-side session alive (throttled)
  useEffect(() => {
    if (!user) return;
    let last = 0;
    const ping = () => {
      const now = Date.now();
      if (now - last < 5 * 60 * 1000) return;
      last = now;
      api.post("/auth/ping").catch(() => {});
    };
    window.addEventListener("click", ping);
    window.addEventListener("keydown", ping);
    return () => {
      window.removeEventListener("click", ping);
      window.removeEventListener("keydown", ping);
    };
  }, [user]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setLogoutReason("");
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setLogoutReason("");
    setUser(false);
  }, []);

  const clearLogoutReason = useCallback(() => setLogoutReason(""), []);

  return <AuthContext.Provider value={{ user, login, logout, logoutReason, clearLogoutReason }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
